import React, { useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  InputBase,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import ListItemRow from './ListItemRow';
import { LIST_CATEGORIES, categoryLabel } from '../../constants/listCategories';

/**
 * One private list rendered as a card: an editable title, a delete button, its
 * items, and an "Add an item…" input at the bottom. Kept presentational — every
 * change is handed up to the page, which owns the optimistic state and the API
 * calls.
 */
const ListCard = ({
  list,
  onRename,
  onDelete,
  onChangeCategory,
  onAddItem,
  onToggleItemDone,
  onEditItemContent,
  onDeleteItem,
}) => {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(list.title);
  const [newItem, setNewItem] = useState('');
  const [moveAnchor, setMoveAnchor] = useState(null);

  const currentCategory = list.category ?? null;
  const currentLabel = categoryLabel(currentCategory);

  const chooseCategory = (key) => {
    setMoveAnchor(null);
    onChangeCategory(list, key);
  };

  const commitTitle = () => {
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (next && next !== list.title) {
      onRename(list, next);
    } else {
      setTitleDraft(list.title);
    }
  };

  const submitItem = () => {
    const content = newItem.trim();
    if (!content) return;
    onAddItem(list, content);
    setNewItem('');
  };

  const items = list.items || [];

  return (
    <Paper elevation={2} sx={{ p: 2, width: 300, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
        {editingTitle ? (
          <InputBase
            value={titleDraft}
            autoFocus
            fullWidth
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
              if (e.key === 'Escape') { setTitleDraft(list.title); setEditingTitle(false); }
            }}
            sx={{ flexGrow: 1, fontSize: '1rem', fontWeight: 600 }}
          />
        ) : (
          <Typography
            variant="subtitle1"
            fontWeight={600}
            onClick={() => { setTitleDraft(list.title); setEditingTitle(true); }}
            sx={{ flexGrow: 1, cursor: 'text', wordBreak: 'break-word' }}
          >
            {list.title}
          </Typography>
        )}

        {currentLabel && (
          <Chip label={currentLabel} size="small" variant="outlined" sx={{ mr: 0.5 }} />
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {items.length}
        </Typography>

        <Tooltip title="Move to">
          <IconButton
            size="small"
            aria-label="Move list to category"
            onClick={(e) => setMoveAnchor(e.currentTarget)}
          >
            <DriveFileMoveOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={moveAnchor} open={Boolean(moveAnchor)} onClose={() => setMoveAnchor(null)}>
          <MenuItem onClick={() => chooseCategory(null)}>
            <ListItemIcon>
              {currentCategory === null ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>None</ListItemText>
          </MenuItem>
          {LIST_CATEGORIES.map(c => (
            <MenuItem key={c.key} onClick={() => chooseCategory(c.key)}>
              <ListItemIcon>
                {currentCategory === c.key ? <CheckIcon fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText>{c.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>

        <Tooltip title="Delete list">
          <IconButton size="small" aria-label="Delete list" onClick={() => onDelete(list)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 1 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {items.map(item => (
          <ListItemRow
            key={item.id}
            item={item}
            onToggleDone={(it, done) => onToggleItemDone(list, it, done)}
            onEditContent={(it, content) => onEditItemContent(list, it, content)}
            onDelete={(it) => onDeleteItem(list, it)}
          />
        ))}
      </Box>

      {items.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Nothing here yet.
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
        <TextField
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitItem(); } }}
          placeholder="Add an item…"
          size="small"
          variant="standard"
          fullWidth
        />
        <Tooltip title="Add item">
          <span>
            <IconButton size="small" aria-label="Add item" onClick={submitItem} disabled={!newItem.trim()}>
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default ListCard;
